import { useCallback, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

type Audience = 'all' | 'selected';

interface SendResult {
    sent: number;
    recipients: number;
}

/**
 * Writes an announcement into users' notification inboxes.
 *
 * The inbox is the delivery path that works: only 8 of 389 users have a push token, so anything
 * sent by push alone is invisible to almost everyone. A row in the inbox always lands, and the
 * user sees it the next time they open the app.
 */
export default function AnnouncementPage() {
    const { token: accessToken } = useAuth();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [link, setLink] = useState('');
    const [audience, setAudience] = useState<Audience>('selected');
    const [userIdsText, setUserIdsText] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<SendResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    /** Broadcasts are irreversible once read, so they get a second, deliberate step. */
    const [confirmingBroadcast, setConfirmingBroadcast] = useState(false);

    const getAuthHeaders = useCallback(() => {
        const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
        if (xsrfCookie) {
            headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
        }
        return headers;
    }, [accessToken]);

    const parsedUserIds = userIdsText
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean);

    const send = useCallback(async () => {
        setSending(true);
        setError(null);
        setResult(null);
        try {
            const response = await axios.post<SendResult>(
                '/api/admin/notifications',
                {
                    title: title.trim(),
                    body: body.trim() || undefined,
                    link: link.trim() || undefined,
                    // Explicit rather than inferred from an empty list: an omitted field is the
                    // likeliest mistake here, and it must send nothing rather than message everyone.
                    all: audience === 'all',
                    user_ids: audience === 'selected' ? parsedUserIds : undefined,
                },
                { headers: getAuthHeaders(), withCredentials: true },
            );
            setResult(response.data);
            setTitle('');
            setBody('');
            setLink('');
            setUserIdsText('');
        } catch (e) {
            const message = axios.isAxiosError(e) ? e.response?.data?.error : null;
            setError(message ?? '발송에 실패했습니다.');
        } finally {
            setSending(false);
            setConfirmingBroadcast(false);
        }
    }, [title, body, link, audience, parsedUserIds, getAuthHeaders]);

    const canSend = title.trim().length > 0
        && (audience === 'all' || parsedUserIds.length > 0);

    return (
        <div className="mx-auto max-w-2xl space-y-6 p-6">
            <header className="space-y-1">
                <h1 className="text-2xl font-bold">공지 발송</h1>
                <p className="text-sm text-muted-foreground">
                    유저의 알림함으로 전달됩니다. 푸시 권한이 없는 유저도 앱을 열면 확인할 수 있습니다.
                </p>
            </header>

            <section className="space-y-3 rounded-md border p-4">
                <div>
                    <label className="text-sm font-semibold" htmlFor="ann-title">제목</label>
                    <input
                        id="ann-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={255}
                        placeholder="알림함 목록에 보이는 한 줄"
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold" htmlFor="ann-body">내용 (선택)</label>
                    <textarea
                        id="ann-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={5}
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold" htmlFor="ann-link">이동 경로 (선택)</label>
                    <input
                        id="ann-link"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder="/ko/coupon"
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm font-mono"
                    />
                    {/*
                      Without a destination the reader knows something happened but not where to
                      act on it, which is most of the point of sending it.
                    */}
                    <p className="mt-1 text-xs text-muted-foreground">
                        비워두면 알림을 눌러도 이동하지 않습니다.
                    </p>
                </div>
            </section>

            <section className="space-y-3 rounded-md border p-4">
                <div className="flex items-center gap-2">
                    <Button
                        variant={audience === 'selected' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAudience('selected')}
                    >
                        개별 지정
                    </Button>
                    <Button
                        variant={audience === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAudience('all')}
                    >
                        전체 발송
                    </Button>
                </div>

                {audience === 'selected' ? (
                    <div>
                        <label className="text-sm font-semibold" htmlFor="ann-users">
                            수신자 <Badge variant="secondary">{parsedUserIds.length}</Badge>
                        </label>
                        <textarea
                            id="ann-users"
                            value={userIdsText}
                            onChange={(e) => setUserIdsText(e.target.value)}
                            rows={4}
                            placeholder="user_id 를 줄바꿈 또는 쉼표로 구분해 입력"
                            className="mt-1 w-full rounded-md border px-3 py-2 font-mono text-xs"
                        />
                    </div>
                ) : (
                    <p className="text-sm text-destructive">
                        저장 기록이 있는 전체 회원에게 발송됩니다.
                    </p>
                )}
            </section>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {result && (
                <p className="text-sm text-emerald-600">
                    {result.sent}명에게 발송했습니다.
                </p>
            )}

            {confirmingBroadcast ? (
                <div className="space-y-3 rounded-md border border-destructive p-4">
                    <p className="text-sm font-semibold">전체 회원에게 발송할까요?</p>
                    {/*
                      A broadcast cannot be recalled once read, and there is no per-user undo.
                      The pause is cheap; a mistaken send to everyone is not.
                    */}
                    <p className="text-xs text-muted-foreground">
                        발송 후에는 취소할 수 없습니다. 제목과 내용을 다시 확인해주세요.
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setConfirmingBroadcast(false)}>
                            돌아가기
                        </Button>
                        <Button onClick={() => void send()} disabled={sending}>
                            발송
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    onClick={() => (audience === 'all' ? setConfirmingBroadcast(true) : void send())}
                    disabled={!canSend || sending}
                >
                    {audience === 'all' ? '전체 발송하기' : `${parsedUserIds.length}명에게 발송`}
                </Button>
            )}
        </div>
    );
}
